export default {
    name: 'OCPP Servers',
    fields: {
        id: 'ID',
        name: 'Name',
        description: 'Description',
        endpointUrl: 'Endpoint URL',
        apiKey: 'API Key', // Keep for backward compatibility
        webhookSecret: 'Webhook Secret', // Keep for backward compatibility
        ocppToBackendApiKey: 'OCPP to Backend API Key',
        backendToOcppApiKey: 'Backend to OCPP API Key',
        elkKey: 'ELK Key',
        status: 'Status',
        environment: 'Environment',
        isPrimary: 'Primary',
        capabilities: 'Capabilities',
        config: 'Configuration',
        ipWhitelist: 'IP Whitelist',
        lastHeartbeat: 'Last Heartbeat',
        version: 'Version',
        loadGroups: 'Load Groups',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
    },
    list: {
        title: 'OCPP Servers',
        filters: {
            search: 'Search by name or description',
        },
    },
    create: {
        title: 'Create OCPP Server',
        subtitle: 'Configure a new OCPP server for charger management',
        backToList: 'Back to OCPP servers list',
        infoMessage: 'OCPP servers manage charging stations and enable load management across distributed systems.',
        basicInfo: 'Basic Information',
        connectionConfig: 'Connection Configuration',
        supabaseConnection: 'Supabase Connection',
        supabaseConnectionHelperText: 'OCPP servers require a Supabase connection to manage charging data and sessions.',
        supabaseConnectionSelectHelperText: 'Select a Supabase connection for this OCPP server',
        securityConfig: 'Security Configuration',
        statusConfig: 'Status Configuration',
        advancedConfig: 'Advanced Configuration',
        advancedConfigMessage: 'Configure advanced server capabilities and settings. Leave empty to use defaults.',
        securityHelperText: 'API keys and webhook secrets will be auto-generated if not provided.',
        nameHelperText: 'Enter a unique, descriptive name for this OCPP server',
        descriptionHelperText: 'Optional description of the server purpose and location',
        endpointUrlHelperText: 'Full URL where the OCPP server or its API is accessible (e.g., ws://localhost:8888 for WebSocket, http://localhost:8787 for HTTP API)',
        statusHelperText: 'Set initial status - typically "pending" for new servers',
        apiKeyHelperText: 'This key authenticates the OCPP server to the backend. Copy and save it securely in your OCPP server configuration.',
        webhookSecretHelperText: 'This secret authenticates backend webhook calls to the OCPP server. Copy and save it securely.',
        ocppToBackendApiKeyHelperText: 'This key is used by the OCPP server to authenticate when calling backend APIs. Copy and save it in your OCPP server\'s configuration file.',
        backendToOcppApiKeyHelperText: 'This key is used by the backend to authenticate when calling OCPP server APIs. It will be stored securely in the backend.',
        keysWarningTitle: 'Important: Save these keys now!',
        keysWarningMessage: 'These keys will be securely stored and cannot be retrieved after creation. Make sure to copy them before saving.',
        keysAutoGenerated: 'Keys Auto-Generated',
        regenerateKeys: 'Regenerate Keys',
        copied: 'Copied!',
        copyToClipboard: 'Copy to clipboard',
        keyWillBeDisplayedAs: 'After saving, this key will be displayed as',
        capabilitiesHelperText: 'JSON object describing server capabilities (e.g., {"supportsLMS": true})',
        configHelperText: 'JSON configuration for server-specific settings',
        ipWhitelistHelperText: 'JSON array of allowed IP ranges (e.g., ["192.168.1.0/24"])',
    },
    edit: {
        title: 'Edit OCPP Server',
        backToList: 'Back to OCPP servers list',
        basicInfo: 'Basic Information',
        connectionConfig: 'Connection Configuration',
        supabaseConnection: 'Supabase Connection',
        supabaseConnectionHelperText: 'Manage the Supabase connection for this OCPP server.',
        supabaseConnectionSelectHelperText: 'Select a new Supabase connection for this server',
        securityConfig: 'Security Configuration',
        statusConfig: 'Status Configuration',
        advancedConfig: 'Advanced Configuration',
        deleteServer: 'Delete Server',
        advancedConfigMessage: 'Configure advanced server capabilities and settings. Leave empty to use defaults.',
        securityWarning: 'Regenerating keys will invalidate existing connections. Update your OCPP server configuration after regenerating.',
        nameHelperText: 'Update the server name (must be unique)',
        descriptionHelperText: 'Update the server description',
        endpointUrlHelperText: 'Update the URL for this OCPP server (WebSocket or HTTP API)',
        statusHelperText: 'Change the server status to control availability',
        apiKeyHelperText: 'Current API key (use regenerate button to create a new one)',
        webhookSecretHelperText: 'Current webhook secret (use regenerate button to create a new one)',
        ocppToBackendApiKeyHelperText: 'Current API key that OCPP server uses to authenticate to backend (use regenerate button to create a new one)',
        backendToOcppApiKeyHelperText: 'Current API key that backend uses to authenticate to OCPP server (use regenerate button to create a new one)',
        capabilitiesHelperText: 'Update server capabilities in JSON format',
        configHelperText: 'Update server configuration in JSON format',
        ipWhitelistHelperText: 'Update IP whitelist in JSON array format',
        regenerateApiKey: 'Regenerate API Key',
        regenerateWebhookSecret: 'Regenerate Webhook Secret',
        regenerateBothKeys: 'Regenerate Both Keys',
        newKeysWarningTitle: 'New keys generated! Save them immediately.',
        newKeysWarningMessage: 'These are your new keys. Copy and update your OCPP server configuration before they disappear.',
        newApiKey: 'New API Key',
        newWebhookSecret: 'New Webhook Secret',
        newOcppToBackendApiKey: 'New OCPP to Backend API Key',
        newBackendToOcppApiKey: 'New Backend to OCPP API Key',
        keysCannotBeRetrieved: 'Keys are securely stored and cannot be retrieved after creation. Use the regenerate button to create new keys if needed.',
        elkKeySection: 'ELK Stack API Key',
        regenerateElkKey: 'Regenerate ELK API Key',
        noElkKeyConfigured: 'No ELK API key configured for this server. ELK logging is disabled.',
        confirmElkKeyRegeneration: 'Confirm ELK Key Regeneration',
        elkKeyRegenerationWarning: 'Regenerating the ELK API key will immediately invalidate the old key. The OCPP server will need to be restarted with the new key to continue logging to Elasticsearch.',
        elkKeyRegenerationConfirm: 'Are you sure you want to regenerate the ELK API key?',
        confirmRegenerate: 'Yes, Regenerate',
        cannotDeleteWithLoadGroups: 'Cannot delete server with {count} assigned load groups',
        deleteServerDescription: 'Permanently delete this OCPP server configuration',
        deleteConfirmTitle: 'Confirm Server Deletion',
        deleteConfirmWarning: 'This will permanently delete the OCPP server configuration.',
        deleteConfirmMessage: 'You are about to permanently delete',
        deleteConfirmInfo: 'This action cannot be undone.',
        deleteConfirmPrompt: 'To confirm deletion, type',
        deleteConfirmPromptBelow: 'below:',
        deleteConfirmTypePlaceholder: 'Type {name} to confirm',
        nameDoesNotMatch: 'Server name does not match',
        deleteButton: 'Delete Server',
    },
    elkKey: {
        title: 'Save New ELK API Key',
        warningTitle: 'Important: Save this key now!',
        warningMessage: 'This new ELK API key will only be shown once and cannot be retrieved later. The old key has been invalidated.',
        label: 'New Elasticsearch API Key',
        helperText: 'Update your OCPP server configuration with this new key and restart the server.',
        confirmButton: 'I have saved the key',
    },
    elkConfig: {
        title: 'ELK Logging Configuration',
        description: 'Configure Elasticsearch logging settings for this OCPP server. These settings control batching, buffering, and spillover behavior.',
        batchSize: 'Batch Size',
        batchSizeHelper: 'Number of logs to batch before sending to Elasticsearch (10-1000). Higher values reduce network overhead.',
        batchTimeout: 'Batch Timeout (ms)',
        batchTimeoutHelper: 'Maximum time in milliseconds to wait before flushing batch (1000-300000). Logs are sent after this timeout even if batch is not full.',
        maxBufferSize: 'Max Buffer Size',
        maxBufferSizeHelper: 'Maximum number of logs to buffer in memory when ELK is unavailable (1000-100000). Prevents memory overflow.',
        spilloverEnabled: 'Enable Spillover',
        spilloverEnabledHelper: 'Write logs to disk when memory buffer is full. Recommended to prevent log loss during ELK outages.',
        spilloverRetention: 'Spillover Retention (days)',
        spilloverRetentionHelper: 'Number of days to retain spillover log files before auto-deletion (1-30). Files are cleaned up automatically after this period.',
    },
    sections: {
        basic: 'Basic Information',
        connection: 'Connection Settings',
        capabilities: 'Server Capabilities',
        configuration: 'Advanced Configuration',
        metadata: 'Metadata',
        dangerZone: 'Danger Zone',
    },
    tabs: {
        general: 'General',
        metadata: 'Metadata',
        danger: 'Danger Zone',
    },
    statuses: {
        pending: 'Pending',
        active: 'Active',
        inactive: 'Inactive',
        maintenance: 'Maintenance',
        retired: 'Retired',
    },
    capabilities: {
        title: 'Server Capabilities',
        description: 'Configure what features this OCPP server supports',
        remoteCommands: 'Remote Commands',
        smartCharging: 'Smart Charging',
        firmwareUpdate: 'Firmware Update',
        localAuthList: 'Local Auth List',
        reservations: 'Reservations',
        lms: 'LMS Support',
        chargingProfiles: 'Charging Profiles',
        none: 'No capabilities configured',
        supportsLMS: 'Supports Load Management System',
        supportsChargingProfiles: 'Supports Charging Profiles',
        maxConcurrentSessions: 'Maximum Concurrent Sessions',
        maxConcurrentSessionsHelper: 'Maximum number of charging sessions this server can handle simultaneously',
        supportsRemoteCommands: 'Supports Remote Commands',
        supportsSmartCharging: 'Supports Smart Charging',
        supportsFirmwareUpdate: 'Supports Firmware Update',
        supportsLocalAuthList: 'Supports Local Auth List',
        supportsReservations: 'Supports Reservations',
    },
    config: {
        title: 'Server Configuration',
        description: 'Configure advanced OCPP server settings. All values have sensible defaults.',
        sections: {
            server: 'Server Configuration',
            lmsCore: 'LMS Core Configuration',
            lmsMessage: 'LMS Message Configuration',
            lmsTime: 'LMS Time Configuration',
        },
        fields: {
            // Server Configuration
            port: 'Server Port',
            logOcppMessages: 'Log OCPP Messages',
            heartbeatInterval: 'Heartbeat Interval',
            meterValueInterval: 'Meter Value Interval',
            enableAuth: 'Enable Authentication',
            enableLMS: 'Enable Load Management',

            // LMS Core Configuration
            rebalanceInterval: 'Load Rebalance Interval',
            minRebalanceInterval: 'Minimum Rebalance Interval',
            lmsIecMinimumCurrent: 'IEC Minimum Current',
            lmsPracticalMinimumCurrent: 'Practical Minimum Current',
            lmsHysteresisThreshold: 'Hysteresis Threshold',
            lmsProfileRefreshInterval: 'Profile Refresh Interval',
            lmsFailSafeCheckInterval: 'Fail-Safe Check Interval',

            // LMS Message Configuration
            lmsMaxRetries: 'Max Retries',
            lmsBaseTimeout: 'Base Timeout',
            lmsBackoffMultiplier: 'Backoff Multiplier',
            lmsMaxQueueSize: 'Max Queue Size',

            // LMS Time Configuration
            lmsDynamicValidityMinutes: 'Dynamic Validity',
            lmsHeartbeatInterval: 'LMS Heartbeat',
            lmsMaxClockDrift: 'Max Clock Drift',
            lmsValidityBuffer: 'Validity Buffer',
        },
        helpers: {
            // Server Configuration
            port: 'WebSocket port for OCPP server to listen on',
            logOcppMessages: 'Enable detailed logging of OCPP protocol messages',
            heartbeatInterval: 'How often the server sends heartbeat messages (milliseconds)',
            meterValueInterval: 'How often meter values are sent (milliseconds)',
            enableAuth: 'Require authentication for OCPP connections',
            enableLMS: 'Enable load management system features',

            // LMS Core Configuration
            rebalanceInterval: 'How often load management rebalancing occurs (milliseconds)',
            minRebalanceInterval: 'Minimum time between rebalance operations (milliseconds)',
            lmsIecMinimumCurrent: 'IEC 61851 minimum current (Amperes)',
            lmsPracticalMinimumCurrent: 'Practical minimum current for fail-safe profiles (Amperes)',
            lmsHysteresisThreshold: 'Minimum current change to apply (Amperes)',
            lmsProfileRefreshInterval: 'How often to refresh charging profiles (milliseconds)',
            lmsFailSafeCheckInterval: 'How often to verify fail-safe profiles (milliseconds)',

            // LMS Message Configuration
            lmsMaxRetries: 'Maximum retry attempts for failed messages',
            lmsBaseTimeout: 'Initial timeout for message responses (milliseconds)',
            lmsBackoffMultiplier: 'Multiplier for retry backoff delay',
            lmsMaxQueueSize: 'Maximum number of queued messages',

            // LMS Time Configuration
            lmsDynamicValidityMinutes: 'Validity period for dynamic charging profiles (minutes)',
            lmsHeartbeatInterval: 'Expected heartbeat interval for LMS (seconds)',
            lmsMaxClockDrift: 'Maximum acceptable clock drift (seconds)',
            lmsValidityBuffer: 'Buffer time for network delays (seconds)',
        },
        resetToDefaults: 'Reset to Defaults',

        // Legacy fields for backward compatibility
        heartbeatInterval: 'Heartbeat Interval (seconds)',
        connectionTimeout: 'Connection Timeout (seconds)',
        messageTimeout: 'Message Timeout (seconds)',
        maxRetries: 'Max Retries',
        retryDelay: 'Retry Delay (seconds)',
        logLevel: 'Log Level',
        enableDebug: 'Enable Debug Mode',
        enableMetrics: 'Enable Metrics Collection',
        customHeaders: 'Custom Headers (JSON)',
    },
    ipWhitelist: {
        title: 'IP Whitelist',
        description: 'Add IP addresses or CIDR ranges that are allowed to connect',
        emptyError: 'Please enter an IP address',
        invalidError: 'Invalid IP address or CIDR notation',
        duplicateError: 'This IP address is already in the list',
        emptyList: 'No IP restrictions configured. All IPs are allowed.',
        helperText: 'Supports IPv4, IPv6, and CIDR notation (e.g., 192.168.1.0/24)',
    },
    active: 'Active',
    helpers: {
        name: 'Unique name for this OCPP server',
        description: 'Optional description of the server purpose',
        status: 'Current operational status',
        endpointUrl: 'Full URL of the OCPP server endpoint (e.g., ws://ocpp.example.com:8888, http://ocpp.example.com:8787)',
        apiKey: 'API key for authenticating with the OCPP server',
        webhookSecret: 'Optional secret for webhook validation',
        connectionTimeout: 'Connection timeout in milliseconds',
        messageTimeout: 'Message timeout in milliseconds',
        maxRetries: 'Maximum number of retry attempts',
        maxConcurrentSessions: 'Maximum number of concurrent charging sessions this server can handle',
    },
    validation: {
        nameTooLong: 'Name must be less than 100 characters',
        descriptionTooLong: 'Description must be less than 500 characters',
        invalidUrl: 'Must be a valid URL starting with ws://, wss://, http://, or https://',
        urlTooLong: 'URL must be less than 500 characters',
        apiKeyTooLong: 'API key must be less than 200 characters',
        webhookSecretTooLong: 'Webhook secret must be less than 200 characters',
    },
    info: {
        connectionNote: 'Ensure the endpoint URL is accessible and the API key is valid before activating',
        capabilitiesNote: 'Enable capabilities based on what your OCPP server supports',
    },
    notifications: {
        createSuccess: 'OCPP server created successfully',
        updateSuccess: 'OCPP server updated successfully',
        deleteSuccess: 'OCPP server deleted successfully',
        deleteError: 'Error deleting OCPP server',
        apiKeyRegenerated: 'API key regenerated successfully',
        apiKeyRegenerateError: 'Failed to regenerate API key',
        webhookSecretRegenerated: 'Webhook secret regenerated successfully',
        webhookSecretRegenerateError: 'Failed to regenerate webhook secret',
        keysRegenerated: 'New keys generated successfully',
        apiKeyCopied: 'API key copied to clipboard',
        webhookSecretCopied: 'Webhook secret copied to clipboard',
        ocppToBackendApiKeyCopied: 'OCPP to Backend API key copied to clipboard',
        backendToOcppApiKeyCopied: 'Backend to OCPP API key copied to clipboard',
    },
    delete: {
        title: 'Delete OCPP Server',
        warning: 'Warning: This action cannot be undone!',
        message: 'You are about to permanently delete the OCPP server',
        content: 'Delete this OCPP server configuration. This action cannot be undone.',
        isActiveContent: 'Cannot delete an active OCPP server. Please set it to inactive first.',
        prompt: 'To confirm deletion, type the server name',
        promptBelow: 'below:',
        typePlaceholder: 'Type server name here',
        nameDoesNotMatch: 'Server name does not match',
        button: 'Delete OCPP Server',
    },
};